import type { Reducer } from 'react';
import React, { useReducer } from 'react';
import { createContext } from 'react';
import type { PageBuilderMeta } from '../types';

interface ModulesContextState {
    showApproveTagHelp: boolean;
    approveTagHelpTagID: number;
}

type ModulesContextActionType = 'resetApproveTagHelp' | 'setApproveTagHelp';

interface ModulesContextAction {
    type: ModulesContextActionType;
    content?: ModulesContextState;
}

type ModulesContextDispatch = (action: ModulesContextAction) => void;

const ModulesContext = createContext<
    | {
          state: ModulesContextState;
          dispatch: ModulesContextDispatch;
          meta: PageBuilderMeta;
      }
    | undefined
>(undefined);

const ModulesContextReducer: Reducer<
    ModulesContextState,
    ModulesContextAction
> = (state, action) => {
    switch (action.type) {
        case 'resetApproveTagHelp':
            return {
                ...state,
                showApproveTagHelp: false,
                approveTagHelpTagID: 0,
            };
        case 'setApproveTagHelp':
            return {
                ...state,
                ...action.content,
            };
    }
};

interface ProviderWrapperProps {
    value?: ModulesContextState;
    children?: React.ReactNode;
    meta: PageBuilderMeta;
}

const ModulesContextProvider = ({
    value,
    children,
    meta,
}: ProviderWrapperProps) => {
    const [state, dispatch] = useReducer(ModulesContextReducer, {
        showApproveTagHelp: value?.showApproveTagHelp || false,
        approveTagHelpTagID: value?.approveTagHelpTagID || 0,
    });
    const values = { state, dispatch, meta };
    return (
        <ModulesContext.Provider value={values}>
            {children}
        </ModulesContext.Provider>
    );
};

const useModulesContext = () => {
    const context = React.useContext(ModulesContext);
    if (context === undefined) {
        throw new Error(
            'useModulesContext must be used within a ModulesContextProvider',
        );
    }
    return context;
};

export { ModulesContextProvider, useModulesContext };
